import React, { useState, useRef } from 'react';
import ComponentExportButton from './ComponentExportButton';
import { ChevronDown, ChevronUp, Lock, Check, X, AlertTriangle, FileText } from 'lucide-react';
import { ComplianceSection } from '../../services/processOptimizationAPI';

interface ComplianceRequirementsProps {
  complianceRequirements: {
    sox_controls: ComplianceSection;
    regulatory_frameworks: ComplianceSection;
    audit_requirements: ComplianceSection;
    data_privacy: ComplianceSection;
    segregation_of_duties: ComplianceSection;
    change_management: ComplianceSection;
    documentation: ComplianceSection;
  };
  implementationComplexity: 'low' | 'medium' | 'high';
}

const ComplianceRequirements: React.FC<ComplianceRequirementsProps> = ({
  complianceRequirements,
  implementationComplexity
}) => {
  const exportRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(implementationComplexity === 'high');

  const getTotalRequirementsCount = () => {
    return Object.values(complianceRequirements).reduce((total, section) => {
      return total + (section.requirements?.length || 0);
    }, 0);
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'medium':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      default:
        return <Check className="w-4 h-4 text-green-600" />;
    }
  };

  const getSectionIcon = (sectionKey: string) => {
    const icons: Record<string, JSX.Element> = {
      sox_controls: <FileText className="w-4 h-4" />,
      regulatory_frameworks: <FileText className="w-4 h-4" />,
      audit_requirements: <FileText className="w-4 h-4" />,
      data_privacy: <Lock className="w-4 h-4" />,
      segregation_of_duties: <FileText className="w-4 h-4" />,
      change_management: <FileText className="w-4 h-4" />,
      documentation: <FileText className="w-4 h-4" />
    };
    return icons[sectionKey] || <FileText className="w-4 h-4" />;
  };

  const getSectionTitle = (sectionKey: string) => {
    const titles: Record<string, string> = {
      sox_controls: 'SOX Controls',
      regulatory_frameworks: 'Regulatory Frameworks',
      audit_requirements: 'Audit Requirements',
      data_privacy: 'Data Privacy & Security',
      segregation_of_duties: 'Segregation of Duties',
      change_management: 'Change Management',
      documentation: 'Documentation & Evidence'
    };
    return titles[sectionKey] || sectionKey;
  };

  const totalCount = getTotalRequirementsCount();

  return (
    <div ref={exportRef} className="mt-4">
      <div
        className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 cursor-pointer hover:bg-yellow-100 transition-colors print:bg-white print:border-gray-300"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-yellow-700" />
            <h4 className="text-sm font-bold text-yellow-900">
              🔒 Compliance & Risk Requirements ({totalCount} requirements)
              {implementationComplexity === 'high' && (
                <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-bold rounded border border-orange-300">
                  ⚠️ HIGH COMPLEXITY
                </span>
              )}
            </h4>
          </div>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-yellow-700" />
          ) : (
            <ChevronDown className="w-5 h-5 text-yellow-700" />
          )}
        </div>
        {!expanded && (
          <p className="text-xs text-yellow-800 mt-2">
            Click to view regulatory considerations, SOX controls, and audit requirements
          </p>
        )}
      </div>

      {expanded && (
        <div className="mt-2 space-y-3 pl-2">
          <div className="flex justify-end">
            <ComponentExportButton targetRef={exportRef} filename="compliance-requirements" />
          </div>
          {Object.entries(complianceRequirements).map(([key, section]) => (
            <div
              key={key}
              className="bg-white border-2 border-gray-200 rounded-lg p-4 shadow-sm print:break-inside-avoid"
            >
              <div className="flex items-start gap-2 mb-2">
                {getSectionIcon(key)}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h5 className="text-sm font-bold text-gray-900">
                      {getSectionTitle(key)}
                    </h5>
                    {getRiskIcon(section.risk_level)}
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                      section.risk_level === 'high'
                        ? 'bg-red-100 text-red-800 border border-red-300'
                        : section.risk_level === 'medium'
                        ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                        : 'bg-green-100 text-green-800 border border-green-300'
                    }`}>
                      {section.risk_level.toUpperCase()} RISK
                    </span>
                  </div>

                  {section.notes && (
                    <p className="text-xs text-gray-700 mb-2 italic">{section.notes}</p>
                  )}

                  {section.frameworks && section.frameworks.length > 0 && (
                    <div className="mb-2">
                      <span className="text-xs font-semibold text-gray-700">Frameworks: </span>
                      <span className="text-xs text-gray-600">{section.frameworks.join(', ')}</span>
                    </div>
                  )}

                  {section.requirements && section.requirements.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs font-semibold text-gray-700 block mb-1">
                        Requirements:
                      </span>
                      <ul className="list-none space-y-1">
                        {section.requirements.map((req, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-gray-700">
                            <span className="text-blue-600 font-bold mt-0.5">•</span>
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {section.applicable === false && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                      <X className="w-3 h-3" />
                      <span>Not applicable to this automation</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ComplianceRequirements;
